import React, { createContext, useContext, useState } from "react";
import { useRouter } from "next/router";
import { useCheckSession } from "@/services/api/auth/queries";
import { useLogin, useLogout, useRegisterParent, useRegisterVendor } from "@/services/api/auth/mutations";
import { useEffectOnce } from "@/hooks/useEffectOnce";
import { getErrorMessage } from "@/lib/utils";
import { Role, SignUpUser, User } from "@/services/api/auth/types";
import { getItem, removeItem, setItem } from "@/lib/localStorage";

export interface AuthContextProps {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  registerUser: (data: SignUpUser, role: Role) => Promise<void>;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);
AuthContext.displayName = "AuthContext";

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const router = useRouter();

  // Initialize hooks
  const { trigger: loginTrigger } = useLogin();
  const { trigger: logoutTrigger } = useLogout();
  const { trigger: registerParentTrigger } = useRegisterParent();
  const { trigger: registerVendorTrigger } = useRegisterVendor();

  // Automatically track authentication state from session data
  const { isMutating, trigger: triggerCheckSession } = useCheckSession();

  // Load user data from localStorage and check session before setting user
  useEffectOnce(() => {
    const handleSessionCheck = async () => {
      try {
        const data = await triggerCheckSession();
        setIsAuthenticated(data.isAuthenticated);
        if (data.isAuthenticated) {
          const storedUser = getItem("user");
          setUser(storedUser);
        } else {
          removeItem("user");
          setUser(null);
        }
      } catch (e) {
        const message = getErrorMessage(e);
        console.error("Check session error:", message);
        removeItem("user");
        setUser(null);
      }
    };

    handleSessionCheck().then();
  });

  async function login(email: string, password: string) {
    if (!email || !password) {
      return;
    }
    try {
      const userData = await loginTrigger({ email, password });
      const authCheck = await triggerCheckSession();
      setUser(userData.user);
      setIsAuthenticated(authCheck.isAuthenticated);
      setItem("user", userData.user);
      const active = userData.user?.active;
      if (!active) {
        await router.push("/verify-email");
        return;
      }

      if (userData.user?.user_type === "parent") {
        await router.push("/explore");
      } else if (userData.user?.user_type === "vendor") {
        await router.push("/vendor");
      } else if (userData.user?.user_type === "admin") {
        await router.push("/admin");
      }
    } catch (e) {
      const message = getErrorMessage(e);
      console.error("Login error:", message);
    }
  }

  async function logout() {
    try {
      await logoutTrigger();
      const authCheck = await triggerCheckSession();
      setUser(null);
      setIsAuthenticated(authCheck.isAuthenticated);
      removeItem("user");
      await router.push("/");
    } catch (e) {
      const message = getErrorMessage(e);
      console.error("Login error:", message);
    }
  }

  async function registerUser(data: SignUpUser, role: Role) {
    if (!data || !role) {
      return;
    }
    try {
      let userData;
      if (role === "parent") {
        userData = await registerParentTrigger({ data });
      } else if (role === "vendor") {
        userData = await registerVendorTrigger({ data });
      } else {
        return;
      }

      await handleUserRegistration(userData, role);
    } catch (e) {
      const message = getErrorMessage(e);
      console.error("Registration error:", message);
    }
  }

  async function handleUserRegistration(userData: User, role: Role) {
    setUser(userData);
    setItem("user", userData);
    const authCheck = await triggerCheckSession();
    setIsAuthenticated(authCheck.isAuthenticated);

    const active = userData?.active;
    if (!active && role === "parent") {
      await router.push("/verify-email");
      return;
    }

    if (role === "parent") {
      await router.replace({ pathname: "/explore", query: { success: true } });
    } else if (role === "vendor") {
      await router.replace("/vendor");
    }
  }

  const value: AuthContextProps = {
    user,
    setUser,
    login,
    logout,
    registerUser,
    isAuthenticated,
    loading: isMutating,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthProvider;
